import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
} from "react-icons/fa";

export default function TopRatedHotels({ limit = 4 }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);

    const navigate = useNavigate();

    // =========================================================
    // Wishlist
    // =========================================================

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some((item) => item.id === id);
    };

    // =========================================================
    // Fetch Top Rated Hotels
    // =========================================================

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get(
                    "https://dubaihotelrates.com/api/top-rated-hotels"
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // =========================================================
    // Render Stars
    // =========================================================

    const renderStars = (rating) => {
        const stars = [];
        const safeRating = Number(rating) || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(
                    <FaStar
                        key={i}
                        className="text-[#FFC928]"
                    />
                );
            } else if (
                i === Math.floor(safeRating) &&
                safeRating % 1 >= 0.5
            ) {
                stars.push(
                    <FaStarHalfAlt
                        key={i}
                        className="text-[#FFC928]"
                    />
                );
            } else {
                stars.push(
                    <FaRegStar
                        key={i}
                        className="text-gray-300"
                    />
                );
            }
        }

        return stars;
    };

    // =========================================================
    // Get Discount
    // =========================================================

    const getDiscount = (hotel) => {
        if (hotel.discount) {
            return hotel.discount;
        }

        if (hotel.discount_percentage) {
            return hotel.discount_percentage;
        }

        return null;
    };

    // =========================================================
    // Loading
    // =========================================================

    if (loading) {
        return (
            <section className="py-12 sm:py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Header Skeleton */}
                    <div className="flex items-center justify-between mb-7">
                        <div>
                            <div className="h-7 w-56 bg-gray-200 rounded animate-pulse"></div>
                        </div>

                        <div className="hidden sm:block h-5 w-28 bg-gray-200 rounded animate-pulse"></div>
                    </div>

                    {/* Cards Skeleton */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((item) => (
                            <div
                                key={item}
                                className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm"
                            >
                                <div className="h-48 bg-gray-200 animate-pulse"></div>

                                <div className="p-4 space-y-3">
                                    <div className="h-5 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-6 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    // =========================================================
    // Render
    // =========================================================

    return (
        <section className="py-12 sm:py-14 lg:py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* =================================================
                    SECTION HEADER
                ================================================== */}

                <div className="flex items-center justify-between mb-6 sm:mb-7">

                    <div>
                        <h2 className="
                            text-2xl
                            sm:text-3xl
                            font-bold
                            text-[#0F172A]
                            tracking-tight
                        ">
                            Popular Hotels in Dubai
                        </h2>
                    </div>

                    {/* Desktop View All */}
                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
                            hidden
                            sm:flex
                            items-center
                            gap-1.5
                            text-sm
                            font-semibold
                            text-[#174EA6]
                            hover:text-[#123D85]
                            transition-colors
                        "
                    >
                        View All Hotels
                        <span className="text-lg leading-none">
                            →
                        </span>
                    </button>
                </div>

                {/* =================================================
                    HOTEL GRID
                ================================================== */}

                <div className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    lg:grid-cols-4
                    gap-4
                    sm:gap-5
                ">
                    {hotels.slice(0, limit).map((hotel) => {

                        const discount = getDiscount(hotel);

                        return (
                            <div
                                key={hotel.id}
                                className="
                                    group
                                    bg-white
                                    rounded-xl
                                    overflow-hidden
                                    border
                                    border-gray-200
                                    shadow-sm
                                    hover:shadow-lg
                                    hover:-translate-y-1
                                    transition-all
                                    duration-300
                                "
                            >

                                {/* =================================================
                                    IMAGE
                                ================================================== */}

                                <div
                                    className="
                                        relative
                                        h-[190px]
                                        sm:h-[180px]
                                        overflow-hidden
                                        cursor-pointer
                                    "
                                    onClick={() =>
                                        navigate(
                                            `/hotels/${hotel.slug}`
                                        )
                                    }
                                >
                                    <img
                                        src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                        alt={hotel.name}
                                        className="
                                            w-full
                                            h-full
                                            object-cover
                                            group-hover:scale-105
                                            transition-transform
                                            duration-500
                                        "
                                    />

                                    {/* Dark subtle overlay */}
                                    <div className="
                                        absolute
                                        inset-0
                                        bg-gradient-to-t
                                        from-black/25
                                        via-transparent
                                        to-transparent
                                        pointer-events-none
                                    " />

                                    {/* Discount Badge */}
                                    {discount && (
                                        <div className="
                                            absolute
                                            top-3
                                            left-3
                                            bg-red-500
                                            text-white
                                            text-[11px]
                                            font-bold
                                            px-2
                                            py-1
                                            rounded-md
                                            shadow-sm
                                        ">
                                            -
                                            {String(discount).replace(
                                                "%",
                                                ""
                                            )}
                                            %
                                        </div>
                                    )}

                                    {/* Wishlist */}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleWishlist(hotel);
                                        }}
                                        className="
                                            absolute
                                            top-3
                                            right-3
                                            w-9
                                            h-9
                                            rounded-full
                                            bg-white/95
                                            backdrop-blur-sm
                                            flex
                                            items-center
                                            justify-center
                                            shadow-md
                                            hover:scale-110
                                            transition-all
                                            duration-200
                                        "
                                        aria-label={
                                            isWishlisted(hotel.id)
                                                ? "Remove from wishlist"
                                                : "Add to wishlist"
                                        }
                                    >
                                        {isWishlisted(hotel.id) ? (
                                            <FaHeart className="text-red-500 text-base" />
                                        ) : (
                                            <FaRegHeart className="text-gray-600 text-base" />
                                        )}
                                    </button>
                                </div>

                                {/* =================================================
                                    CARD CONTENT
                                ================================================== */}

                                <div className="p-4">

                                    {/* Hotel Name */}
                                    <h3
                                        onClick={() =>
                                            navigate(
                                                `/hotels/${hotel.slug}`
                                            )
                                        }
                                        className="
                                            text-[15px]
                                            font-bold
                                            text-[#0F172A]
                                            hover:text-[#174EA6]
                                            cursor-pointer
                                            truncate
                                            transition-colors
                                            duration-200
                                        "
                                        title={hotel.name}
                                    >
                                        {hotel.name}
                                    </h3>

                                    {/* Rating */}
                                    <div className="
                                        flex
                                        items-center
                                        gap-2
                                        mt-2
                                    ">
                                        <div className="
                                            flex
                                            items-center
                                            gap-0.5
                                            text-xs
                                        ">
                                            {renderStars(hotel.rating)}
                                        </div>

                                        <span className="
                                            text-xs
                                            font-semibold
                                            text-gray-600
                                        ">
                                            {hotel.rating || "0"}
                                        </span>
                                    </div>

                                    {/* Location + Price */}
                                    <div className="
                                        flex
                                        items-end
                                        justify-between
                                        gap-3
                                        mt-3
                                    ">

                                        {/* Location */}
                                        <div className="
                                            flex
                                            items-center
                                            gap-1.5
                                            min-w-0
                                        ">
                                            <FaMapMarkerAlt
                                                className="
                                                    text-[#174EA6]
                                                    text-xs
                                                    flex-shrink-0
                                                "
                                            />

                                            <span
                                                className="
                                                    text-xs
                                                    text-gray-500
                                                    truncate
                                                "
                                                title={hotel.location}
                                            >
                                                {hotel.location}
                                            </span>
                                        </div>

                                        {/* Price */}
                                        <div className="
                                            flex-shrink-0
                                            text-right
                                        ">
                                            <span className="
                                                text-[10px]
                                                text-gray-400
                                                block
                                            ">
                                                From
                                            </span>

                                            <div className="
                                                flex
                                                items-baseline
                                                justify-end
                                                gap-1
                                            ">
                                                <span className="
                                                    text-lg
                                                    font-extrabold
                                                    text-[#174EA6]
                                                ">
                                                    AED{" "}
                                                    {Math.round(
                                                        hotel.price_per_night
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Per Night */}
                                    <div className="
                                        text-[10px]
                                        text-gray-400
                                        text-right
                                        mt-0.5
                                    ">
                                        per night
                                    </div>

                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* =================================================
                    MOBILE VIEW ALL
                ================================================== */}

                <div className="mt-6 sm:hidden">
                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
                            w-full
                            flex
                            items-center
                            justify-center
                            gap-2
                            py-3
                            rounded-xl
                            border
                            border-[#174EA6]
                            text-[#174EA6]
                            font-semibold
                            text-sm
                            hover:bg-[#174EA6]
                            hover:text-white
                            transition-all
                            duration-300
                        "
                    >
                        View All Hotels
                        <span className="text-lg">
                            →
                        </span>
                    </button>
                </div>

                {/* =================================================
                    SLIDER DOTS - DESIGN ACCENT
                ================================================== */}

                <div className="
                    flex
                    items-center
                    justify-center
                    gap-2
                    mt-6
                ">
                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-[#174EA6]
                    "></span>

                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-gray-300
                    "></span>

                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-gray-300
                    "></span>

                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-gray-300
                    "></span>

                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-gray-300
                    "></span>

                    <span className="
                        w-2
                        h-2
                        rounded-full
                        bg-gray-300
                    "></span>
                </div>

            </div>
        </section>
    );
}   